import { getBatchDetails } from '@/layouts/cjr-layout/actions';
import { getCampaignParams, getPaidParams, getSource, getUserParams } from '@/lib/events/helper';
import { ServerAnalyticsPlatform } from '@/types/auth';
import { BatchDetailModel } from '@/types/batch';
import { AppOtplessConfig, UserType } from '@/types/types';
import { validateName } from '@admin/lib/validation.utility';
import { EVENT_PLATFORMS, EVENTS_ORG } from '@pw-tech/web-sdk';
import { format } from 'date-fns';
import { ReadonlyURLSearchParams } from 'next/navigation';
import { curiousJrBatchIds, HOSTNAMES, ORG_CONFIG_MAP } from '../constants/constants';

export const getUserTypeFromPaidBatch = (paid_batch_type?: string): UserType => {
  if (!paid_batch_type || paid_batch_type.trim() === '') {
    return UserType.Free;
  }

  const type = paid_batch_type.toLowerCase();

  switch (type) {
    case 'booster':
      return UserType.Demo;
    case 'both':
    case 'master':
      return UserType.Master;
    default:
      return UserType.Free;
  }
};

export const loadScriptById = (id: string, src: string) => {
  return new Promise(resolve => {
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    document.body.appendChild(script);
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
  });
};

export const findBatchType = (batchDetails: { config?: { cjrBatchType?: string } }) => {
  return batchDetails?.config?.cjrBatchType === 'booster' ? 'booster' : 'master';
};

export const getStartingScheduleOfBatch = (batchDetail: BatchDetailModel) => {
  return batchDetail?.subjects
    ?.map(subject => {
      return subject?.schedules;
    })
    ?.flat()
    ?.sort((a, b) => new Date(a?.startTime).getTime() - new Date(b?.startTime).getTime())?.[0];
};

export const trackPixelEvent = (name: string, data: any) => {
  if (typeof window !== 'undefined') {
    const fbq = (window as any)?.fbq;
    if (fbq) {
      // Standard Facebook Pixel events
      const standardEvents = [
        'ViewContent',
        'Search',
        'AddToCart',
        'AddToWishlist',
        'InitiateCheckout',
        'AddPaymentInfo',
        'Purchase',
        'Lead',
        'CompleteRegistration',
        'Contact',
        'CustomizeProduct',
        'Donate',
        'FindLocation',
        'Schedule',
        'StartTrial',
        'SubmitApplication',
        'Subscribe',
      ];

      // Map custom events to standard ones where appropriate
      const eventMapping: { [key: string]: string } = {
        signup_success: 'CompleteRegistration',
        login_success: 'Lead',
        class_filter_select: 'ViewContent',
        mweb_enroll_click: 'InitiateCheckout',
        batch_enroll_free: 'CompleteRegistration',
      };

      const mappedEvent = eventMapping[name];

      if (mappedEvent && standardEvents.includes(mappedEvent)) {
        // Use standard event
        fbq('track', mappedEvent, data);
      } else if (standardEvents.includes(name)) {
        // Already a standard event
        fbq('track', name, data);
      } else {
        // Use trackCustom for non-standard events to avoid warnings
        fbq('trackCustom', name, data);
      }
    }
  }
};

export const getCurrencyIcon = (currency: string) => {
  switch (currency) {
    case 'INR':
      return '₹';
    case 'USD':
      return '$';
    case 'AED':
      return 'AED';
    case 'QAR':
      return 'QAR';
    case 'SAR':
      return 'SAR';
    case 'BHD':
      return 'BHD';
    case 'KWD':
      return 'KWD';
    case 'OMR':
      return 'OMR';
    default:
      return currency;
  }
};

/**
 * Get parsed localStorage value from key
 * Provide default value for better error handling
 */
export const getLocalStorageValue = (key: string, defaultValue?: any) => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const localStored = localStorage.getItem(key);
      if (typeof defaultValue === 'string' || defaultValue === undefined || defaultValue === null) {
        return localStored || defaultValue;
      }
      // JSON.parse only works for valid objects
      const val = (localStored && JSON.parse(localStored)) || defaultValue;
      return val;
    }
    return defaultValue;
  } catch (error) {
    console.error(`Error getting localStorage value for key "${key}":`, error);
    return localStorage.getItem(key) || defaultValue;
  }
};

/**
 *
 * @param str
 * @returns gives an array of number from the string
 * Example -: Class 6 -> [6]
 *            Grade 6 -> [6]
 *            6th -> [6]
 */
export const getNumbersFromString = (str: string) => {
  if (!str) return [];
  return str.match(/\d+/g)?.map(Number);
};

export const isStudentNameValid = (name: string, isMessageReturned = false) => {
  let message = '';

  if (!name) {
    message = 'Please enter your name!';
  } else if (name.length < 3) {
    message = 'Name should be atleast 3 characters long!';
  } else if (name.toLowerCase() === 'null') {
    message = `Name can't be ${name}`;
  } else if (validateName(name)) {
    message = 'Please enter a valid name!';
  }

  // return isMessageReturned ? message : true
  if (isMessageReturned) {
    return message;
  }
  return !!message;
};

export const isOTPValid = (phone: string, otpLength?: number) => {
  const _text = phone || '';
  const pattern = new RegExp(`\\d{${otpLength || getOtpLength()}}$`);
  return pattern.test(_text);
};

/**
 * Checks if a value is empty.
 * Returns true if the value is empty, otherwise false.
 */
export const isEmpty = (value: object | string | null | undefined) => {
  // Check for null, undefined, or empty string
  if (value == null || value === undefined || value === '') return true;

  // Check for empty array
  if (Array.isArray(value) && value.length === 0) return true;

  // Check for empty object
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value).length === 0;
  }

  // Non-empty
  return false;
};

export const getAnalyticsParams = () => {
  const source = getSource();
  const { user_status: userStatus, paid_batch_type: paidBatchType } = getPaidParams({
    is_paid_batch_type_required: true,
    is_user_status_required: true,
  });
  const { user_cohort: userCohort } = getUserParams(true);
  const {
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_content: utmContent,
    gclid,
    fbclid,
  } = getCampaignParams();

  return {
    source,
    userStatus,
    paidBatchType,
    userCohort,
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    gclid,
    fbclid,
  };
};

export const getBatchInfo = (start?: string, end?: string) => {
  if (!start || !end) return { label: '', dateRange: '' };

  const formattedStart = format(new Date(start), 'd MMM yyyy');
  const formattedEnd = format(new Date(end), 'd MMM yyyy');

  return {
    label: formattedStart !== formattedEnd ? 'Course Duration:' : 'Class Date:',
    dateRange:
      formattedStart !== formattedEnd
        ? `${format(new Date(start), 'd MMM')} - ${formattedEnd}`
        : formattedStart,
  };
};
export const getUserName = (user: any): string => {
  return user?.lastName ? `${user?.firstName}${user?.lastName}` : user?.firstName || '';
};

export const handleTabClick = (
  selectedTab: string,
  setActiveTab: (tab: string) => void,
  setActiveClass: (className: string | null) => void
) => {
  setActiveTab(selectedTab);
  setActiveClass(null);
  const componentId = selectedTab.toLowerCase().replace(/\s+/g, '-');
  const element = document.getElementById(componentId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

export const getAuthSdkConfigByOrg = (organizationDetails: any) => {
  return {
    organizationId: organizationDetails?.organizationId || '',
    apiBaseUrl: organizationDetails?.apiBaseURL || '',
    loginMethods: organizationDetails?.loginMethods,
    localStorageFallback: true,
    clientId: 'system-admin',
    clientSecret: 'KjPXuAVfC5xbmgreETNMaL7z',
    contextIdentifier: organizationDetails?.contextIdentifier || '',
    loginPageUrl: '/',
    debugMode: false,
    subOrgId: 'SUB-PWLI000',
    unleashConfig: {
      unleashClientKey: `${process.env.NEXT_PUBLIC_UNLEASH_CLIENT_KEY}`,
      unleashProxyUrl: `${process.env.NEXT_PUBLIC_UNLEASH_PROXY_URL}`,
      appName: 'gcms-admin',
      analyticsEndpointUrl: `${process.env.NEXT_PUBLIC_ANALYTICS_ENDPOINT_URL}`,
    },
    eventConfig: {
      eventPlatforms: organizationDetails?.eventPlatforms || [EVENT_PLATFORMS.GA],
      organizationName: organizationDetails?.organizationName as EVENTS_ORG,
      eventsDebugMode: !isEnvProduction(),
    },
  };
};

export const HOSTNAME_ALIAS_MAP: Record<string, Record<string, string>> = {
  DEVELOPMENT: {
    'dev.physicswallah.live': HOSTNAMES.PHYSICS_WALLAH,
    'dev.curiousjr.com': HOSTNAMES.CURIOUS_JR,
    'pwioi-dev.pwioi.com': HOSTNAMES.PHYSICS_WALLAH_IOI,
  },
  STAGING: {
    'staging.physicswallah.live': HOSTNAMES.PHYSICS_WALLAH,
    'staging.curiousjr.com': HOSTNAMES.CURIOUS_JR,
    'pwioi-staging.pwioi.com': HOSTNAMES.PHYSICS_WALLAH_IOI,
    'staging.pwmedharthi.com': HOSTNAMES.PHYSICS_WALLAH_MEDHARTHI,
    'staging.thegurukulam.school': HOSTNAMES.PHYSICS_WALLAH,
    'staging.careerkaptain.com': HOSTNAMES.PHYSICS_WALLAH,
    'pw-unigo-stage.penpencil.co': HOSTNAMES.PHYSICS_WALLAH,
  },
  'PRE-PROD': {
    'demo.pw.live': HOSTNAMES.PHYSICS_WALLAH,
    'pre-prod.curiousjr.com': HOSTNAMES.CURIOUS_JR,
    'pwioi-pre-prod.pwioi.com': HOSTNAMES.PHYSICS_WALLAH_IOI,
    'pw-unigo-pre-prod.penpencil.co': HOSTNAMES.PHYSICS_WALLAH,
  },
  PRODUCTION: {
    'pw.live': HOSTNAMES.PHYSICS_WALLAH,
    'curiousjr.com': HOSTNAMES.CURIOUS_JR,
    'pwioi.com': HOSTNAMES.PHYSICS_WALLAH_IOI,
    'pwmedharthi.com': HOSTNAMES.PHYSICS_WALLAH_MEDHARTHI,
    'thegurukulam.school': HOSTNAMES.PHYSICS_WALLAH,
    'careerkaptain.com': HOSTNAMES.PHYSICS_WALLAH,
    'acadfly.com': HOSTNAMES.PHYSICS_WALLAH,
  },
};

export const getOrgConfigByHost = (host: string) => {
  const env = process.env.NEXT_PUBLIC_ENVIRONMENT || 'PRODUCTION';
  const envMap = HOSTNAME_ALIAS_MAP[env] || HOSTNAME_ALIAS_MAP['PRODUCTION'];

  const logicalHost = envMap[host] || 'default';
  const config =
    ORG_CONFIG_MAP[
      (logicalHost in ORG_CONFIG_MAP ? logicalHost : 'default') as keyof typeof ORG_CONFIG_MAP
    ];

  return {
    ...config,
    matchedHost: logicalHost,
  };
};

export function getMatchingClassFromBreadcrumb(
  breadcrumbs: { link?: string }[],
  classesList: { name: string; url: string }[]
): string | undefined {
  const link = breadcrumbs[1]?.link;
  if (!link) return undefined;
  const numberMatch = link.match(/\d+/);
  if (!numberMatch) return undefined;
  const matchingClass = classesList.find(({ name }) =>
    name.trim().toLowerCase().endsWith(numberMatch[0].toLowerCase())
  );
  return matchingClass?.name;
}

export const setUtmParams = (searchParams: ReadonlyURLSearchParams | null) => {
  const utmKeys = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'gclid',
    'fbclid',
    'igshid',
  ];

  const utmParams = {} as { [key: string]: string };
  let shouldUpdate = false;
  utmKeys.forEach(key => {
    utmParams[key] = searchParams?.get(key) || '';
    if (searchParams?.get(key)) {
      shouldUpdate = true;
    }
  });

  if (shouldUpdate) {
    localStorage.setItem('utmParams', JSON.stringify(utmParams));
  }
};

export const getRefreshTokenFromLocalStorage = () => {
  return getLocalStorageValue('REFRESH_TOKEN') || null;
};

export const isFromApp = () => {
  if (typeof window === 'undefined') return false;

  const searchParams = new URLSearchParams(window.location.search);

  const fromAppParam = searchParams.get('from_app');

  return fromAppParam === 'true' || getLocalStorageValue('from_app') === 'true';
};

export const fetchPlatform = () => {
  if (typeof window === 'undefined') return 'WEB';

  const searchParams = new URLSearchParams(window.location.search);
  const platformParam = searchParams.get('platform');

  return platformParam === 'ios' ? ServerAnalyticsPlatform.IOS : ServerAnalyticsPlatform.ANDROID;
};

export const invokeAppMethod = (methodName: string, ...methodArgs: any[]): any => {
  if (!isFromApp()) {
    return;
  }
  if (typeof window !== 'undefined') {
    console.log(`calling native method: ${methodName} with arguments: ${methodArgs}`);

    // android jsbridge callbacks
    try {
      if (typeof (window as any).JSBridge?.[methodName] === 'function') {
        if (methodArgs.length) {
          return (window as any).JSBridge[methodName](...methodArgs);
        } else {
          return (window as any).JSBridge[methodName]();
        }
      }
      // iOS webkit callbacks
      else if (
        typeof (window as any).webkit?.messageHandlers?.[methodName]?.postMessage === 'function'
      ) {
        if (methodArgs.length) {
          return (window as any).webkit.messageHandlers[methodName].postMessage(...methodArgs);
        } else {
          return (window as any).webkit.messageHandlers[methodName].postMessage(JSON.stringify({}));
        }
      }
    } catch (err) {
      console.log(
        `error in calling native method: ${methodName} with arguments: ${methodArgs
          .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
          .join(',   ')}, err: ${err}`
      );
      return;
    }
  }
};

export const getOtpLength = () => {
  return isFromApp() ? 4 : 6;
};

export const getCurrEnvOtplessConfig = (
  otplessConfigs: AppOtplessConfig[]
): AppOtplessConfig | { appId: string } => {
  const mapAppEnvToWebEnv = (env: string) => {
    switch (env) {
      case 'prod':
        return 'PRODUCTION';
      case 'staging':
        return 'STAGING';
      case 'preprod':
        return 'PRE-PROD';
      default:
        return '';
    }
  };
  const currentEnv = process.env.NEXT_PUBLIC_ENVIRONMENT || '';

  const envConfig = (otplessConfigs || []).find(
    config => mapAppEnvToWebEnv(config.env) === currentEnv
  );

  return (
    envConfig || {
      appId: '',
    }
  );
};

export const checkNewUser = (userDetails?: any) => {
  const user = userDetails || getLocalStorageValue('user', {});
  if (isEmpty(user)) return true;

  const firstName = user?.firstName || '';
  const grade = user?.profileId?.class || '';
  const cohortId = user?.profileId?.cohortId || '';

  // Check if firstName, grade, or cohortId exists
  return !firstName || !grade || !cohortId;
};

export const setAdditionalPageData = async (data: any[]) => {
  const additionalPageData: any = {};
  // data for BatchCard.Marketing.Landing
  if (!data) return additionalPageData;
  const batchConfig = data.find((item: any) => item['id'] === 'BatchCard.Marketing.Landing');
  if (batchConfig?.data?.classes?.length) {
    const batchIds = batchConfig?.data?.classes?.map((item: any) => item?.batchId);
    additionalPageData['BatchCard.Marketing.Landing'] = await Promise.all(
      batchIds.map((batchId: string) => getBatchDetails(batchId)?.then((batch: any) => batch?.data))
    );
  }
  return additionalPageData;
};

export const isEnvProduction = () => process.env.NEXT_PUBLIC_ENVIRONMENT === 'PRODUCTION';
export const validateCuriousJrBatch = (id: string | null | undefined) => {
  if (!id) return false;
  return curiousJrBatchIds.includes(id);
};

/**
 *
 * @param classes array of strings representing class numbers e.g. ['1', '2', '3']
 * @returns removed array format in string format e.g. ['1', '2', '3'] -> 1,2,3 or ['4', '0', '3'] -> 4,0,3
 */
export const getEventClasses = (classes: string[]): string => {
  return JSON.stringify((classes || []).map(Number)).slice(1, -1);
};
